/**
 * CL-ACCESS-CONTROL-PLANE-003C — Canonical portal effective-access resolver.
 *
 * Single source for client-portal tab visibility and admin preview/badge copy.
 * Capability-driven; role labels do not grant or deny.
 */

import { resolveShouldUseSharedFilesOnlyForPortalFiles } from "./planningCapabilityResolver";
import { projectAccountingPermissionsUiEnabled } from "./projectAccountingPermissionsFeatureGate";
import { resolveClientPortalFutureModuleVisibility } from "./clientPortalModuleApplicabilityModel";
import { CLIENT_SAFE_INVOICE_PERMISSION_KEYS } from "./clientSafeInvoicePermissionKeys";
import {
  PROJECT_INVOICE_CAPABILITY_KEYS,
} from "./projectInvoicePermissionsEditorModel";
import { PROJECT_TABS } from "./projectPortalTabsModel";
import {
  assignmentUsesStaffFinancialPortalTabModel,
  buildStaffFinancialSubtabs,
} from "./projectStaffFinancialPortalModel";
import { evaluateStaffFinancialCapabilities } from "./projectStaffFinancialAuthorizationModel";
import {
  CLIENT_SUPPLIER_MODULE_PERMISSION_KEY,
  CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY,
} from "./projectSupplierPermissionsEditorModel";
import { readCanManageTradeDirectory } from "./staffCapabilitiesModel";
import { resolveSchedulePortalCapabilities } from "./projectScheduleCapabilityModel";
import {
  clientPortalSpecificationsTabAllowed,
  clientPortalTradesSuppliersTabAllowed,
} from "./clientPortalProjectOperatorAccessModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

function isEffectiveFullInvoiceAccess(caps = {}) {
  return PROJECT_INVOICE_CAPABILITY_KEYS.every((key) => caps[key] === true);
}

function uniquePreserve(values = []) {
  const out = [];
  const seen = new Set();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const line = safeStr(value);
    if (!line || seen.has(line)) return;
    seen.add(line);
    out.push(line);
  });
  return out;
}

export function clientHasTradesSuppliersModuleAccess(permissions = {}) {
  return (
    permissions?.canViewSuppliersAndSelections === true ||
    permissions?.[CLIENT_SUPPLIER_MODULE_PERMISSION_KEY] === true
  );
}

export function collaboratorHasProjectSupplierManagementAccess(permissions = {}) {
  return (
    permissions?.canViewProjectSuppliers === true ||
    permissions?.canManageProjectSuppliers === true
  );
}

export function resolveCollaboratorPortalEffectiveAccess({
  permissions = {},
  assignment = null,
  project = null,
  accountingPermissionsUiEnabled = null,
  isClientPersona = false,
  isContributorPersona = false,
  isPastProject = false,
  isSharedFilesOnlyViewer = false,
  packageAvailable = false,
  linkedUserProfile = null,
} = {}) {
  const resolvedAssignment = assignment || {
    permissions,
    accessScope: "full_project",
    status: "active",
    role: isClientPersona ? "client" : "collaborator",
  };
  const mergedPermissions = {
    ...(resolvedAssignment.permissions || {}),
    ...permissions,
  };
  const caps = evaluateStaffFinancialCapabilities({
    assignment: { ...resolvedAssignment, permissions: mergedPermissions },
    accountingPermissionsUiEnabled,
  });
  const accountingEnabled = projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled);
  const useStaffFinancialPortal =
    accountingEnabled &&
    assignmentUsesStaffFinancialPortalTabModel({
      assignment: { ...resolvedAssignment, permissions: mergedPermissions },
      accountingPermissionsUiEnabled,
    });
  const futureModules = resolveClientPortalFutureModuleVisibility({
    permissions: mergedPermissions,
    project,
  });
  const scheduleCaps = resolveSchedulePortalCapabilities({
    isAdmin: false,
    isClientPersona,
    permissions: mergedPermissions,
    assignment: resolvedAssignment,
  });
  const sharedFilesOnly = resolveShouldUseSharedFilesOnlyForPortalFiles({
    matchedCollaborator: { ...resolvedAssignment, permissions: mergedPermissions },
  });
  const clientTradesModule = clientHasTradesSuppliersModuleAccess(mergedPermissions);
  const projectSupplierMgmt = collaboratorHasProjectSupplierManagementAccess(mergedPermissions);
  const globalDirectory = readCanManageTradeDirectory(linkedUserProfile) === true;
  const staffFinancialSubtabs =
    useStaffFinancialPortal && caps.canAccessFinancials
      ? buildStaffFinancialSubtabs({
          assignment: { ...resolvedAssignment, permissions: mergedPermissions },
          accountingPermissionsUiEnabled,
        }).map((tab) => tab.key)
      : [];

  const showPlanning =
    mergedPermissions.canViewPlanning === true &&
    (!isSharedFilesOnlyViewer || packageAvailable) &&
    !isPastProject;
  const showDocuments =
    mergedPermissions.canUploadFiles === true &&
    ((isClientPersona && !isContributorPersona) || isContributorPersona || !isClientPersona);

  const tabs = {
    [PROJECT_TABS.OVERVIEW]: !isPastProject,
    [PROJECT_TABS.PLANNING]: showPlanning,
    [PROJECT_TABS.PHOTOS]: mergedPermissions.canViewPhotos === true && !isPastProject,
    [PROJECT_TABS.DRAWINGS_REVIEW]: mergedPermissions.canReviewDrawings === true,
    [PROJECT_TABS.DOCUMENTS_REQUESTS]: showDocuments && !isPastProject,
    [PROJECT_TABS.TIME_LOG]:
      !isPastProject &&
      !useStaffFinancialPortal &&
      (mergedPermissions.canLogTime === true || mergedPermissions.canViewTimeLog === true),
    [PROJECT_TABS.INVOICES]:
      !isPastProject &&
      !useStaffFinancialPortal &&
      mergedPermissions.canManageInvoices === true &&
      !isClientPersona,
    [PROJECT_TABS.FINANCIALS]:
      !isPastProject &&
      ((useStaffFinancialPortal && caps.canAccessFinancials) ||
        (isClientPersona && !isContributorPersona && futureModules.financials)),
    [PROJECT_TABS.TRADES_SUPPLIERS]: !isPastProject && clientPortalTradesSuppliersTabAllowed(mergedPermissions),
    [PROJECT_TABS.SPECIFICATIONS]:
      !isPastProject
      && clientPortalSpecificationsTabAllowed(mergedPermissions, {
        ...resolvedAssignment,
        permissions: mergedPermissions,
      }),
    [PROJECT_TABS.SCHEDULE]:
      !isPastProject &&
      ((isClientPersona && !isContributorPersona && futureModules.schedule) ||
        (isContributorPersona && scheduleCaps.canViewSchedule === true)),
    [PROJECT_TABS.CONSTRUCTION]:
      !isPastProject && isClientPersona && !isContributorPersona && futureModules.construction,
    [PROJECT_TABS.RESOURCES]:
      !isPastProject && isClientPersona && !isContributorPersona && futureModules.resources,
    [PROJECT_TABS.MESSAGES]:
      !isPastProject && isClientPersona && !isContributorPersona && futureModules.messages,
  };

  const invoiceCaps = caps;
  const staffInvoiceWorkflow =
    invoiceCaps.canViewInvoices === true ||
    invoiceCaps.canPrepareInvoices === true ||
    invoiceCaps.canReviewInvoices === true ||
    invoiceCaps.canApproveInvoices === true ||
    invoiceCaps.canShareClientTest === true ||
    invoiceCaps.canIssueInvoices === true ||
    mergedPermissions.canManageInvoices === true;

  return {
    permissions: mergedPermissions,
    caps,
    invoiceCaps,
    useStaffFinancialPortal,
    staffFinancialSubtabs,
    clientTradesModule,
    projectSupplierMgmt,
    globalDirectory,
    sharedFilesOnly,
    staffInvoiceWorkflow,
    tabs,
  };
}

export function buildCollaboratorPortalEffectiveAccessPreviewLines({
  permissions = {},
  assignment = null,
  project = null,
  accountingPermissionsUiEnabled = null,
  isClientPersona = false,
  isContributorPersona = false,
  isPastProject = false,
  isSharedFilesOnlyViewer = false,
  packageAvailable = false,
  linkedUserProfile = null,
} = {}) {
  const context = resolveCollaboratorPortalEffectiveAccess({
    permissions,
    assignment,
    project,
    accountingPermissionsUiEnabled,
    isClientPersona,
    isContributorPersona,
    isPastProject,
    isSharedFilesOnlyViewer,
    packageAvailable,
    linkedUserProfile,
  });
  const p = context.permissions;
  const lines = [];

  lines.push("Project overview");

  if (context.tabs[PROJECT_TABS.PLANNING]) {
    lines.push("Planning package");
  }

  if (context.tabs[PROJECT_TABS.DRAWINGS_REVIEW]) {
    lines.push(context.sharedFilesOnly ? "Shared drawings" : "Project drawings");
    lines.push("Drawing comments where enabled");
  }

  if (p.canUploadFiles === true) {
    lines.push("Revision upload where enabled");
    lines.push("Engineering / supporting document upload");
  }

  if (context.tabs[PROJECT_TABS.PHOTOS]) {
    lines.push("Project photos shared in the portal");
  }

  if (
    context.tabs[PROJECT_TABS.TIME_LOG] ||
    (context.useStaffFinancialPortal && context.staffFinancialSubtabs.includes("time_log"))
  ) {
    lines.push("Time Log");
  }

  if (p.canManageCollaborators === true) {
    lines.push("Collaborator administration");
  }

  if (context.useStaffFinancialPortal && context.caps.canAccessFinancials) {
    lines.push("Staff Financials workspace");
  }

  if (context.caps.canViewBudget || context.caps.canManageBudget) {
    lines.push(context.caps.canManageBudget ? "Budget (manage access)" : "Budget (view access)");
  }

  if (context.staffInvoiceWorkflow) {
    lines.push(
      isEffectiveFullInvoiceAccess(context.invoiceCaps)
        ? "Full staff invoice workflow"
        : "Staff invoice workflow"
    );
  }

  if (context.tabs[PROJECT_TABS.SCHEDULE]) {
    lines.push(p.canEditSchedule === true ? "Schedule: Edit" : "Schedule: View");
  }

  if (context.clientTradesModule) {
    lines.push("Client Trades & suppliers");
  }

  if (p[CLIENT_SUPPLIER_CONTRIBUTE_PERMISSION_KEY] === true) {
    lines.push("Add supplier updates on shared suppliers");
  }

  if (context.projectSupplierMgmt) {
    lines.push("Project supplier management");
  }

  if (context.globalDirectory) {
    lines.push("Global company directory (Staff Access capability)");
  }

  if (p[CLIENT_SAFE_INVOICE_PERMISSION_KEYS.issued] === true) {
    lines.push("Client-safe issued invoice access");
  }

  if (p[CLIENT_SAFE_INVOICE_PERMISSION_KEYS.clientTest] === true) {
    lines.push("Client-safe client-test invoice access");
  }

  if (context.tabs[PROJECT_TABS.FINANCIALS] && !context.useStaffFinancialPortal) {
    lines.push("Client Financials module");
  }

  return uniquePreserve(lines);
}

export function buildCollaboratorPortalEffectiveAccessBadgeLabels({
  permissions = {},
  assignment = null,
  project = null,
  accountingPermissionsUiEnabled = null,
  isClientPersona = false,
  isContributorPersona = false,
  linkedUserProfile = null,
} = {}) {
  const context = resolveCollaboratorPortalEffectiveAccess({
    permissions,
    assignment,
    project,
    accountingPermissionsUiEnabled,
    isClientPersona,
    isContributorPersona,
    linkedUserProfile,
  });
  const badges = [];

  const push = (label) => {
    if (label && !badges.includes(label)) badges.push(label);
  };

  if (context.tabs[PROJECT_TABS.PLANNING]) push("Planning");
  if (context.tabs[PROJECT_TABS.DRAWINGS_REVIEW]) push("Drawings");
  if (context.tabs[PROJECT_TABS.PHOTOS]) push("Photos");
  if (
    context.tabs[PROJECT_TABS.TIME_LOG] ||
    (context.useStaffFinancialPortal && context.staffFinancialSubtabs.includes("time_log"))
  ) {
    push("Time Log");
  }

  if (projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    if (context.useStaffFinancialPortal && context.caps.canAccessFinancials) {
      push("Staff Financials workspace");
    }
    if (context.caps.canViewBudget || context.caps.canManageBudget) push("Budget");
    if (context.staffInvoiceWorkflow) {
      push(
        isEffectiveFullInvoiceAccess(context.invoiceCaps)
          ? "Full staff invoice workflow"
          : "Staff invoice workflow"
      );
    }
  } else if (context.permissions?.canManageInvoices === true) {
    push("Staff invoice workflow");
  }

  if (context.clientTradesModule) push("Client Trades & suppliers");
  if (context.projectSupplierMgmt) push("Project supplier management");
  if (context.globalDirectory) push("Global company directory");

  if (
    context.permissions?.[CLIENT_SAFE_INVOICE_PERMISSION_KEYS.issued] === true ||
    context.permissions?.[CLIENT_SAFE_INVOICE_PERMISSION_KEYS.clientTest] === true
  ) {
    push("Client-safe invoice access");
  }

  if (!badges.length) push("Limited access");
  return badges;
}

export function shouldShowClientTradesSuppliersPortalTab(input = {}) {
  return resolveCollaboratorPortalEffectiveAccess(input).tabs[PROJECT_TABS.TRADES_SUPPLIERS] === true;
}

export { PROJECT_INVOICE_CAPABILITY_KEYS };
